import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackLeadConversion } from '../../lib/gtag';
import { getUtm } from '../../lib/utm';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, ChevronLeft, Send, CheckCircle, Package, Scale, ShoppingCart, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    key: 'cargo',
    icon: Package,
    title: 'Що плануєте везти з Китаю?',
    options: ['Одяг / взуття', 'Електроніка', 'Товари для дому', 'Автозапчастини', 'Інше'],
  },
  {
    key: 'weight',
    icon: Scale,
    title: 'Орієнтовна вага вантажу?',
    options: ['До 10 кг', '10–50 кг', '50–200 кг', '200+ кг'],
  },
  {
    key: 'vykup',
    icon: ShoppingCart,
    title: 'Чи потрібен викуп товару у постачальника?',
    options: ['Так, потрібен викуп', 'Ні, лише доставка'],
  },
];

const DONE_KEY = 'cargoags_quiz_done';
const CLOSED_KEY = 'cargoags_quiz_closed_at';

export const QuizPopup = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DONE_KEY)) return;
      const closedAt = parseInt(localStorage.getItem(CLOSED_KEY) || '0', 10);
      if (closedAt && Date.now() - closedAt < 24 * 60 * 60 * 1000) return;
    } catch { /* private mode */ }
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(CLOSED_KEY, String(Date.now())); } catch {}
  };

  const pick = (value) => {
    setAnswers(a => ({ ...a, [STEPS[step].key]: value }));
    setStep(s => s + 1);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || name.trim().length < 2) { toast.error("Введіть ім'я"); return; }
    if (!phone.trim() || phone.trim().length < 5) { toast.error('Введіть телефон'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        phone: phone.trim(),
        message: `Квіз: Вантаж — ${answers.cargo} · Вага — ${answers.weight} · Викуп — ${answers.vykup}`,
        source: 'quiz',
        website,
        ...getUtm(),
      });
      trackLeadConversion();
      setSent(true);
      try { localStorage.setItem(DONE_KEY, '1'); } catch {}
      setTimeout(() => setOpen(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Помилка надсилання');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const isContact = step >= STEPS.length;
  const progress = Math.round(((sent ? STEPS.length + 1 : step) / (STEPS.length + 1)) * 100);
  const Icon = isContact ? Phone : STEPS[step].icon;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70" data-testid="quiz-popup-overlay">
      <div className="relative w-full max-w-md rounded-2xl border border-blue-500/40 bg-[#0a1224] shadow-[0_0_60px_-10px_rgba(59,130,246,0.7)] overflow-hidden animate-slideUpFade" data-testid="quiz-popup">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
        <button onClick={close} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-white hover:border-blue-400 transition-colors flex items-center justify-center" aria-label="Закрити" data-testid="quiz-close">
          <X className="w-4 h-4" />
        </button>

        {/* progress */}
        <div className="h-1.5 bg-slate-800">
          <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div className="p-6">
          {sent ? (
            <div className="text-center py-8" data-testid="quiz-success">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(16,185,129,0.6)] mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-white font-bold text-xl mb-1">Дякуємо!</h4>
              <p className="text-slate-300 text-sm">Менеджер зв'яжеться з вами протягом 15 хвилин з розрахунком вартості</p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-5">
                <div className="w-11 h-11 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.5)] flex-shrink-0">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-cyan-400 text-[11px] font-bold uppercase tracking-wider mb-0.5">
                    {isContact ? 'Останній крок' : `Крок ${step + 1} з ${STEPS.length + 1}`}
                  </p>
                  <h3 className="text-white font-bold text-lg leading-tight">
                    {isContact ? 'Куди надіслати розрахунок вартості?' : STEPS[step].title}
                  </h3>
                </div>
              </div>

              {!isContact ? (
                <div className="space-y-2.5" data-testid={`quiz-step-${step}`}>
                  {STEPS[step].options.map(opt => (
                    <button key={opt} onClick={() => pick(opt)}
                      className="w-full text-left px-4 py-3 rounded-xl border border-blue-500/30 bg-slate-900/60 text-slate-200 text-sm font-medium hover:border-cyan-400 hover:bg-blue-950/60 hover:text-white transition-colors"
                      data-testid="quiz-option">
                      {opt}
                    </button>
                  ))}
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="quiz-contact-form">
                  <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)}
                    style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
                    tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <Input placeholder="Ваше ім'я" value={name} onChange={e => setName(e.target.value)}
                    className="h-11 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="quiz-name" required />
                  <Input type="tel" placeholder="+380 ... (телефон або Telegram)" value={phone} onChange={e => setPhone(e.target.value)}
                    className="h-11 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="quiz-phone" required />
                  <Button type="submit" disabled={submitting}
                    className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
                    data-testid="quiz-submit">
                    {submitting ? 'Відправляю...' : (<><Send className="w-4 h-4 mr-2" />Отримати розрахунок</>)}
                  </Button>
                  <p className="text-[10px] text-slate-500 text-center">Натискаючи кнопку, ви погоджуєтесь на обробку даних</p>
                </form>
              )}

              {step > 0 && !sent && (
                <button onClick={() => setStep(s => s - 1)} className="mt-4 inline-flex items-center gap-1 text-slate-400 hover:text-cyan-400 text-xs transition-colors" data-testid="quiz-back">
                  <ChevronLeft className="w-3.5 h-3.5" /> Назад
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
